import z from 'zod';

export const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB for images
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB for documents
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/webp',
  'image/gif',
] as const;
export const UPLOAD_MIME_TYPES = ['ImageJpg', 'ImagePng', 'ImageWebp', 'ImageGif', 'ApplicationPdf'] as const;

export type AvailableFileMimeType = (typeof ACCEPTED_MIME_TYPES)[number];
export type UploadFileMimeType = (typeof UPLOAD_MIME_TYPES)[number];

// Map all file types to existing image types as a workaround
export const MIME_TYPE_MAP: Record<AvailableFileMimeType, UploadFileMimeType> = {
  'application/pdf': 'ApplicationPdf',
  'image/jpeg': 'ImageJpg',
  'image/png': 'ImagePng',
  'image/jpg': 'ImageJpg',
  'image/webp': 'ImageWebp',
  'image/gif': 'ImageGif',
};

export type UploadFileContextType = {
  error: string | null;
  loading: boolean;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  onUploadFileComplete: (url: string) => Promise<void> | void;
};

export type UploadFileContextProps = {
  onError: (error: string) => void;
  onLoading: (loading: boolean) => void;
};

export type UploadFileResultType = {
  path: string;
  putFileUrl: string;
  url: string;
};

export const imageSchema = z.object({
  type: z.enum(ACCEPTED_MIME_TYPES, { errorMap: () => ({ message: 'Invalid file type' }) }),
  size: z.number().max(MAX_IMAGE_SIZE, 'Image size is too large'),
});

export const fileSchema = z.object({
  type: z.enum(ACCEPTED_MIME_TYPES, { errorMap: () => ({ message: 'Invalid file type' }) }),
  size: z.number().max(MAX_FILE_SIZE, 'File size is too large'),
});

/**
 * Builds a file validation schema for `useUploadFile.upload`. Callers that accept only the standard
 * document/image MIME set with the shared 10MB cap can omit options (matches `fileSchema`). Areas
 * with their own rules — e.g. the file_upload block, which accepts any type up to a 50MB per-block
 * cap and relies on a server-side blocklist — pass `{ maxSize, anyType: true }`.
 *
 * Kept separate from `fileSchema` so that schema's `type` stays narrowed to the MIME enum for
 * callers that index by it; this factory's return is intentionally widened by the `anyType` branch.
 */
export const makeFileSchema = ({
  maxSize = MAX_FILE_SIZE,
  anyType = false,
}: { maxSize?: number; anyType?: boolean } = {}) =>
  z.object({
    type: anyType ? z.string() : z.enum(ACCEPTED_MIME_TYPES, { errorMap: () => ({ message: 'Invalid file type' }) }),
    size: z.number().max(maxSize, 'File size is too large'),
  });
